// On-screen piano for note entry: click a key to append that pitch to the
// melody (and hear it). A simple, dependency-free keyboard — white keys in a
// row, black keys overlaid. See task #19.

import { getChordPlayer } from '@/audio/player';

export interface PianoInputProps {
  onNote: (midi: number) => void;
  /** Lowest MIDI note (default C3 = 48). */
  low?: number;
  /** Number of octaves to show (default 2). */
  octaves?: number;
}

const WHITE_PC = [0, 2, 4, 5, 7, 9, 11];
const BLACK = [
  { pc: 1, after: 0 }, // C#
  { pc: 3, after: 1 }, // D#
  { pc: 6, after: 3 }, // F#
  { pc: 8, after: 4 }, // G#
  { pc: 10, after: 5 }, // A#
];
const NAME = ['C', '', 'D', '', 'E', 'F', '', 'G', '', 'A', '', 'B'];

export function PianoInput({ onNote, low = 48, octaves = 2 }: PianoInputProps) {
  const startOctave = Math.floor(low / 12) - 1;
  const whiteKeys: number[] = [];
  for (let o = 0; o < octaves; o++) {
    for (const pc of WHITE_PC) whiteKeys.push((startOctave + 1 + o) * 12 + pc);
  }
  const whiteW = 100 / whiteKeys.length;

  const hit = (midi: number) => {
    onNote(midi);
    try {
      void getChordPlayer().playNote(midi, 0.5);
    } catch {
      /* no audio (e.g. tests) */
    }
  };

  return (
    <div
      className="piano-input"
      data-testid="piano-input"
      role="group"
      aria-label="On-screen piano"
      style={{ position: 'relative', height: 90, userSelect: 'none', maxWidth: 640 }}
    >
      {whiteKeys.map((midi, i) => (
        <button
          key={midi}
          type="button"
          data-midi={midi}
          aria-label={`${NAME[midi % 12]}${Math.floor(midi / 12) - 1}`}
          onClick={() => hit(midi)}
          style={{
            position: 'absolute',
            left: `${i * whiteW}%`,
            width: `${whiteW}%`,
            height: '100%',
            background: 'var(--surface-2, #fff)',
            border: '1px solid var(--border, #bbb)',
            borderRadius: '0 0 4px 4px',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            fontSize: 10,
            color: 'var(--text-muted, #999)',
            cursor: 'pointer',
            padding: '0 0 4px',
          }}
        >
          {midi % 12 === 0 ? `C${Math.floor(midi / 12) - 1}` : ''}
        </button>
      ))}
      {Array.from({ length: octaves }, (_, o) =>
        BLACK.map(({ pc, after }) => {
          const midi = (startOctave + 1 + o) * 12 + pc;
          const whiteIndex = o * 7 + after;
          return (
            <button
              key={midi}
              type="button"
              data-midi={midi}
              aria-label={`${['', 'C#', '', 'D#', '', '', 'F#', '', 'G#', '', 'A#'][pc] || 'sharp'}${Math.floor(midi / 12) - 1}`}
              onClick={() => hit(midi)}
              style={{
                position: 'absolute',
                left: `${(whiteIndex + 1) * whiteW - whiteW * 0.3}%`,
                width: `${whiteW * 0.6}%`,
                height: '62%',
                background: 'var(--text-primary, #222)',
                border: '1px solid #000',
                borderRadius: '0 0 3px 3px',
                cursor: 'pointer',
                zIndex: 2,
              }}
            />
          );
        }),
      )}
    </div>
  );
}
